import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type UIEvent,
} from 'react';
import { highlightSnippet, SHIKI_THEME } from '../../../lib/shiki';
import type { EditorTab } from '../types';
import { ContextMenu, type ContextMenuItem } from './ContextMenu';

interface EditorPaneProps {
  tabs: EditorTab[];
  activeTabId: string;
  onSelectTab: (id: string) => void;
  onNewTab: () => void;
  onCloseTab: (id: string) => void;
  onRenameTab: (id: string, title: string) => void;
  onCloseOtherTabs: (keepId: string) => void;
  onCloseAllTabs: () => void;
  value: string;
  onChange: (next: string) => void;
  /** Run a specific SQL string. */
  onRun: (sql: string) => void;
  /** Format the given SQL. Returns the formatted text, or null on failure
   *  (the host shows the error itself). */
  onFormat: (sql: string) => Promise<string | null>;
  busy: boolean;
  disabled: boolean;
  history: string[];
}

const PLACEHOLDER = `-- ⌘/Ctrl+Enter — run statement at cursor
-- ⌘/Ctrl+Shift+Enter — run entire buffer

SELECT 'hello, world' AS greeting;`;

// Tokenize the source into top-level statement bounds (split on `;`,
// ignoring semicolons inside string literals or line/block comments),
// then return the statement the cursor falls inside. If the cursor is in
// trailing whitespace after the final `;`, fall back to the last
// non-empty statement so "click Run with cursor at end" Just Works.
function statementAtCursor(source: string, offset: number): string {
  const len = source.length;
  const stmts: { start: number; end: number }[] = [];
  let i = 0;
  let stmtStart = 0;
  while (i < len) {
    const ch = source[i];
    const next = source[i + 1];
    if (ch === '-' && next === '-') {
      const nl = source.indexOf('\n', i);
      i = nl === -1 ? len : nl + 1;
      continue;
    }
    if (ch === '/' && next === '*') {
      const close = source.indexOf('*/', i + 2);
      i = close === -1 ? len : close + 2;
      continue;
    }
    if (ch === "'" || ch === '"') {
      const quote = ch;
      i++;
      while (i < len) {
        const c = source[i];
        if (c === '\\') {
          i += 2;
          continue;
        }
        if (c === quote) {
          if (source[i + 1] === quote) {
            i += 2;
            continue;
          }
          i++;
          break;
        }
        i++;
      }
      continue;
    }
    if (ch === ';') {
      stmts.push({ start: stmtStart, end: i + 1 });
      stmtStart = i + 1;
      i++;
      continue;
    }
    i++;
  }
  if (stmtStart < len) stmts.push({ start: stmtStart, end: len });
  if (stmts.length === 0) return source.trim();

  for (const s of stmts) {
    if (offset >= s.start && offset <= s.end) {
      const trimmed = source.slice(s.start, s.end).trim();
      if (trimmed) return trimmed;
    }
  }
  for (let k = stmts.length - 1; k >= 0; k--) {
    const s = stmts[k];
    if (!s) continue;
    const trimmed = source.slice(s.start, s.end).trim();
    if (trimmed) return trimmed;
  }
  return '';
}

export function EditorPane({
  tabs,
  activeTabId,
  onSelectTab,
  onNewTab,
  onCloseTab,
  onRenameTab,
  onCloseOtherTabs,
  onCloseAllTabs,
  value,
  onChange,
  onRun,
  onFormat,
  busy,
  disabled,
  history,
}: EditorPaneProps) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const preRef = useRef<HTMLPreElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const themeName = SHIKI_THEME;
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showHistory, setShowHistory] = useState(false);
  const [renamingTabId, setRenamingTabId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [formatBusy, setFormatBusy] = useState(false);
  const [menu, setMenu] = useState<{
    pos: { x: number; y: number };
    items: ContextMenuItem[];
  } | null>(null);

  const submitRename = () => {
    if (renamingTabId) onRenameTab(renamingTabId, renameDraft);
    setRenamingTabId(null);
    setRenameDraft('');
  };

  const formatDocument = useCallback(async () => {
    if (formatBusy || disabled || !value.trim()) return;
    setFormatBusy(true);
    const next = await onFormat(value);
    setFormatBusy(false);
    if (next != null && next !== value) onChange(next);
  }, [disabled, formatBusy, onChange, onFormat, value]);

  const formatSelection = useCallback(async () => {
    const ta = taRef.current;
    if (!ta || formatBusy || disabled) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    if (start === end) {
      // Nothing selected — fall back to the whole document.
      await formatDocument();
      return;
    }
    const before = value.slice(0, start);
    const selected = value.slice(start, end);
    const after = value.slice(end);
    setFormatBusy(true);
    const formatted = await onFormat(selected);
    setFormatBusy(false);
    if (formatted == null || formatted === selected) return;
    const next = before + formatted + after;
    onChange(next);
    queueMicrotask(() => {
      const t = taRef.current;
      if (!t) return;
      // Re-select the freshly formatted range so the user can see what
      // changed and re-format / undo if needed.
      const newEnd = start + formatted.length;
      t.selectionStart = start;
      t.selectionEnd = newEnd;
      t.focus();
    });
  }, [disabled, formatBusy, formatDocument, onChange, onFormat, value]);

  // External system: Shiki tokenizer is loaded lazily. We highlight the
  // current buffer asynchronously and render the result in the overlay <pre>.
  useEffect(() => {
    let cancelled = false;
    const source = value.endsWith('\n') ? value : value + '\n';
    highlightSnippet(source, 'sql', themeName).then((html) => {
      if (!cancelled) setHighlighted(html);
    });
    return () => {
      cancelled = true;
    };
  }, [value, themeName]);

  // External system: keep the highlight overlay + line-number gutter aligned
  // with the textarea on input/scroll. Layout effect so the user never sees
  // a frame of misaligned scroll positions.
  useLayoutEffect(() => {
    const ta = taRef.current;
    const pre = preRef.current;
    const gut = gutterRef.current;
    if (!ta) return;
    if (pre) {
      pre.scrollTop = ta.scrollTop;
      pre.scrollLeft = ta.scrollLeft;
    }
    if (gut) gut.scrollTop = ta.scrollTop;
  }, [value]);

  const handleScroll = useCallback((e: UIEvent<HTMLTextAreaElement>) => {
    const pre = preRef.current;
    const gut = gutterRef.current;
    if (pre) {
      pre.scrollTop = e.currentTarget.scrollTop;
      pre.scrollLeft = e.currentTarget.scrollLeft;
    }
    if (gut) gut.scrollTop = e.currentTarget.scrollTop;
  }, []);

  const runCurrent = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    const offset = ta.selectionStart ?? value.length;
    const stmt = statementAtCursor(value, offset);
    if (stmt) onRun(stmt);
  }, [value, onRun]);

  const runAll = useCallback(() => {
    const sql = value.trim();
    if (sql) onRun(sql);
  }, [value, onRun]);

  const insertTab = useCallback(
    (ta: HTMLTextAreaElement) => {
      const start = ta.selectionStart ?? 0;
      const end = ta.selectionEnd ?? start;
      const next = value.slice(0, start) + '  ' + value.slice(end);
      onChange(next);
      queueMicrotask(() => {
        const t = taRef.current;
        if (!t) return;
        t.selectionStart = t.selectionEnd = start + 2;
      });
    },
    [value, onChange],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === 'Enter') {
        e.preventDefault();
        if (e.shiftKey) runAll();
        else runCurrent();
        return;
      }
      if (mod && e.shiftKey && (e.key === 'F' || e.key === 'f')) {
        e.preventDefault();
        // ⌘/Ctrl+Shift+F — JetBrains' Reformat shortcut. Honors selection.
        const ta = e.currentTarget;
        if ((ta.selectionStart ?? 0) !== (ta.selectionEnd ?? 0)) {
          formatSelection();
        } else {
          formatDocument();
        }
        return;
      }
      if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        insertTab(e.currentTarget);
        return;
      }
      if (mod && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        if (history.length === 0) return;
        e.preventDefault();
        if (e.key === 'ArrowUp') {
          const next = historyIndex < 0 ? history.length - 1 : Math.max(0, historyIndex - 1);
          setHistoryIndex(next);
          onChange(history[next] ?? '');
        } else {
          if (historyIndex < 0) return;
          const next = historyIndex + 1;
          if (next >= history.length) {
            setHistoryIndex(-1);
            onChange('');
          } else {
            setHistoryIndex(next);
            onChange(history[next] ?? '');
          }
        }
      }
    },
    [
      formatDocument,
      formatSelection,
      history,
      historyIndex,
      insertTab,
      onChange,
      runAll,
      runCurrent,
    ],
  );

  const openTabMenu = (e: React.MouseEvent, tabId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const tab = tabs.find((t) => t.id === tabId);
    if (!tab) return;
    setMenu({
      pos: { x: e.clientX, y: e.clientY },
      items: [
        {
          label: formatBusy ? 'Formatting…' : 'Format document',
          onClick: () => {
            // If the menu was triggered from a tab that isn't currently
            // active, switch to it first so we format the right buffer.
            if (tabId !== activeTabId) onSelectTab(tabId);
            // Format on the next tick so the active-tab change has time
            // to propagate; otherwise we'd format the previously-active
            // tab's content.
            queueMicrotask(formatDocument);
          },
          disabled: formatBusy || !tab.sql.trim(),
        },
        { separator: true, label: '' },
        {
          label: 'Rename…',
          onClick: () => {
            setRenamingTabId(tabId);
            setRenameDraft(tab.title);
          },
        },
        { separator: true, label: '' },
        { label: 'Close', onClick: () => onCloseTab(tabId) },
        {
          label: 'Close others',
          onClick: () => onCloseOtherTabs(tabId),
          disabled: tabs.length <= 1,
        },
        {
          label: 'Close all',
          onClick: onCloseAllTabs,
          disabled: tabs.length === 0,
        },
      ],
    });
  };

  const openEditorMenu = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const ta = e.currentTarget;
    const hasSelection = (ta.selectionStart ?? 0) !== (ta.selectionEnd ?? 0);
    setMenu({
      pos: { x: e.clientX, y: e.clientY },
      items: [
        {
          label: formatBusy ? 'Formatting…' : 'Format document',
          onClick: formatDocument,
          disabled: formatBusy || !value.trim(),
        },
        {
          label: 'Format selection',
          onClick: formatSelection,
          disabled: formatBusy || !hasSelection,
        },
        { separator: true, label: '' },
        {
          label: 'Run statement at cursor',
          onClick: runCurrent,
          disabled: busy || !value.trim(),
        },
        {
          label: 'Run entire buffer',
          onClick: runAll,
          disabled: busy || !value.trim(),
        },
      ],
    });
  };

  // Compute line numbers from the current buffer. A trailing newline (the
  // one we add to the highlight overlay) shouldn't get its own number.
  const lineNumbers = useMemo(() => {
    const lines = value.split('\n').length;
    return Array.from({ length: lines }, (_, i) => i + 1);
  }, [value]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-[#2b2b2b]">
      <div className="flex shrink-0 items-stretch border-b border-[#323232] bg-[#3c3f41]">
        <div className="flex flex-1 items-stretch overflow-x-auto">
          {tabs.map((tab) => {
            const isActive = tab.id === activeTabId;
            const isRenaming = renamingTabId === tab.id;
            return (
              <div
                key={tab.id}
                className={
                  'group flex shrink-0 items-center gap-1 border-r border-[#323232] px-2 text-[11px] ' +
                  (isActive
                    ? 'bg-[#2b2b2b] text-[#e8e8e8]'
                    : 'bg-[#3c3f41] text-[#bbbbbb] hover:bg-[#4c5052]')
                }
              >
                {isRenaming ? (
                  <input
                    autoFocus
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitRename();
                      if (e.key === 'Escape') {
                        setRenamingTabId(null);
                        setRenameDraft('');
                      }
                    }}
                    onBlur={submitRename}
                    className="w-32 rounded border border-[#5394ec] bg-[#2b2b2b] px-1 py-0.5 text-[11px] text-[#e8e8e8] outline-none"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => onSelectTab(tab.id)}
                    onContextMenu={(e) => openTabMenu(e, tab.id)}
                    onDoubleClick={() => {
                      setRenamingTabId(tab.id);
                      setRenameDraft(tab.title);
                    }}
                    title={`${tab.title} (double-click to rename · right-click for more)`}
                    className="max-w-[180px] truncate py-1.5"
                  >
                    {tab.title}
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCloseTab(tab.id);
                  }}
                  aria-label="Close tab"
                  title="Close tab"
                  className="rounded px-1 text-[#606366] opacity-0 hover:bg-[#4c5052] hover:text-[#e8e8e8] group-hover:opacity-100"
                >
                  ×
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => onNewTab()}
            title="New query tab"
            aria-label="New query tab"
            className="px-2 text-[#bbbbbb] hover:bg-[#4c5052] hover:text-[#e8e8e8]"
          >
            +
          </button>
        </div>
      </div>
      <div className="relative flex flex-1 overflow-hidden">
        <div
          ref={gutterRef}
          aria-hidden="true"
          className="select-none overflow-hidden border-r border-[#323232] bg-[#313335] py-3 pl-2 pr-2 text-right font-mono text-[12px] leading-5 text-[#606366]"
          style={{ minWidth: '3em' }}
        >
          {lineNumbers.map((n) => (
            <div key={n} className="tabular-nums">
              {n}
            </div>
          ))}
        </div>
        <div className="relative flex-1 overflow-hidden">
          <pre
            ref={preRef}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 m-0 overflow-auto whitespace-pre py-3 pl-3 pr-3 font-mono text-[13px] leading-5 text-[#a9b7c6]"
            style={{ tabSize: 2 }}
            dangerouslySetInnerHTML={highlighted ? { __html: highlighted } : undefined}
          >
            {!highlighted ? value : null}
          </pre>
          <textarea
            ref={taRef}
            value={value}
            onChange={(e) => {
              setHistoryIndex(-1);
              onChange(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            onScroll={handleScroll}
            onContextMenu={openEditorMenu}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            disabled={disabled}
            className="absolute inset-0 m-0 h-full w-full resize-none whitespace-pre bg-transparent py-3 pl-3 pr-3 font-mono text-[13px] leading-5 text-transparent caret-[#a9b7c6] outline-none placeholder:text-[#606366] disabled:opacity-50"
            style={{
              WebkitTextFillColor: 'transparent',
              tabSize: 2,
            }}
            placeholder={PLACEHOLDER}
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-[#323232] bg-[#3c3f41] px-3 py-1.5">
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-wide text-[#808080]">
            ⌘/Ctrl+Enter · Shift for all · ⇧F to format
          </span>
          {history.length > 0 && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowHistory((v) => !v)}
                className="rounded border border-[#4a4a4a] bg-[#2b2b2b] px-2 py-0.5 text-[11px] text-[#bbbbbb] hover:border-[#5a5a5a] hover:bg-[#3c3f41]"
              >
                History · {history.length} ▾
              </button>
              {showHistory && (
                <div className="absolute bottom-full left-0 z-20 mb-1 max-h-64 w-80 overflow-auto rounded border border-[#4a4a4a] bg-[#2b2b2b] p-1 shadow-xl">
                  {history
                    .slice()
                    .reverse()
                    .map((entry, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          onChange(entry);
                          setShowHistory(false);
                        }}
                        className="block w-full truncate rounded px-2 py-1 text-left font-mono text-[11px] text-[#bbbbbb] hover:bg-[#4c5052] hover:text-[#e8e8e8]"
                        title={entry}
                      >
                        {entry.replace(/\s+/g, ' ').slice(0, 120)}
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-stretch overflow-hidden rounded border border-[#4a4a4a]">
          <button
            type="button"
            onClick={runCurrent}
            disabled={disabled || busy || !value.trim()}
            className="flex items-center gap-1.5 bg-[#499c54] px-3 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-[#5fb069] disabled:cursor-not-allowed disabled:bg-[#3c3f41] disabled:text-[#606366]"
            title="Run statement at cursor (⌘/Ctrl+Enter)"
          >
            <svg width="9" height="9" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true">
              <path d="M2 1 L8 5 L2 9 Z" />
            </svg>
            {busy ? 'Running…' : 'Run'}
          </button>
          <button
            type="button"
            onClick={runAll}
            disabled={disabled || busy || !value.trim()}
            className="border-l border-[#4a4a4a] bg-[#3c3f41] px-2 py-1 text-[11px] text-[#bbbbbb] transition-colors hover:bg-[#4c5052] hover:text-[#e8e8e8] disabled:cursor-not-allowed disabled:opacity-50"
            title="Run entire buffer (⌘/Ctrl+Shift+Enter)"
          >
            Run all
          </button>
        </div>
      </div>
      <ContextMenu
        position={menu?.pos ?? null}
        items={menu?.items ?? []}
        onClose={() => setMenu(null)}
      />
    </div>
  );
}
